import { defineStore } from 'pinia';
import type {
  SquareBadgeComponentProps,
  CardComponentProps,
  ModalOpenProps,
} from '@i22team/td-longtail-vue-components';

export type Staggered = {
  amount: number;
  percentage: number;
};

export type Rebate = {
  name?: string;
  description?: string | undefined; // Visible as badge text
  theme?: SquareBadgeComponentProps['theme'];
  footnote?: string;
  staggereds?: Staggered[];
};

export type Rebates = Record<string, Rebate>;

export type OfferBadgeEntry = {
  articleNumber: string;
  listText?: string;
  badge?: SquareBadgeComponentProps;
  card?: CardComponentProps; // is highlighted
  modal?: ModalOpenProps; // highlight is clickable
};

export interface OfferState {
  rebatesState: Rebates;
  offerBadgesState: OfferBadgeEntry[];
  zeroPercentFinancingState: string[];
  compaEanListState: string[];
}

const badgeEntries = (
  articleNumbers: string[],
  badge: SquareBadgeComponentProps,
  listText?: string
): OfferBadgeEntry[] => articleNumbers.map((articleNumber) => ({ articleNumber, badge, listText }));

const rebatesToOfferBadges = (rebates: Rebates): OfferBadgeEntry[] =>
  Object.entries(rebates)
    .filter(([, rebate]) => !!rebate.description)
    .map(([articleNumber, rebate]) => ({
      articleNumber,
      listText: 'AKTION',
      badge: {
        text: rebate.description as string,
        footnoteText: rebate.footnote,
        theme: rebate.theme,
      },
    }));

// We use the Staggered rebates. But they cannot be combined with Coupons. So we need to add the texte by hand.
export const manualOfferBadges = ref<OfferBadgeEntry[]>([
  // (https://i22-telekom-longtail.atlassian.net/browse/SM-175)
  ...badgeEntries(
    [
      // Samsung Galaxy Z Flip8
      // 256
      '99937746', // Graphite
      '99937747', // Cream
      '99937750', // Pink

      // 512
      '99937752', // Graphite
      '99937755', // Cream
      '99937757', // Pink
    ],
    {
      text: '200&nbsp;€ Ankaufsbonus',
      footnoteText: `<div class="unstyled-html"><h4>Aktionsbedingungen</h4><br><ul>
<li>Der Ankaufsbonus gilt für private Endkunden ab 18 Jahren bei Kauf eines Samsung Galaxy Z Flip8, Samsung Galaxy Z Fold8 oder Samsung Galaxy Z Fold8 Ultra vom 22.07.2026-06.08.2026 bei der Telekom Deutschland GmbH (TDG) und bei zusätzlichem Verkauf eines teilnahmeberechtigten Smartphones an die Assurant Deutschland GmbH. </li>
<li>Das Handyankaufsangebot für das Altgerät muss bis zum 06.08.2026 generiert werden und das Altgerät bis zum 27.08.2026 bei der Assurant Deutschland GmbH bzw. ihrem Dienstleister angekommen sein.</li>
<li>Teilnehmer erhalten ein Ankaufsangebot für das Altgerät, zzgl. eines Ankaufsbonus. 200&nbsp;€ Ankaufsbonus gibt es beim Kauf eines Samsung Galaxy Z Flip8 und 400&nbsp;€ Ankaufsbonus gibt es beim Kauf eines Samsung Galaxy Z Fold8 oder Samsung Galaxy Z Fold8 Ultra. Das Altgerät muss funktionsfähig sein, d.h. es muss ein- und ausschaltbar sowie entsperrt (kein SIM-/Net-Lock) sein, darf keinen Wasserschaden oder Displaybruch aufweisen und muss einen Mindestankaufswert von 1&nbsp;€ haben.</li>
<li>Teilnahme mit max. 3 Aktionsgeräten pro Haushalt. Solange der Vorrat reicht.</li>
<li>Ausrichter der Aktion ist Telekom Deutschland GmbH, Landgrabenweg 149, 53227 Bonn, Deutschland.</li>
<li>Hier finden Sie die <a href="/downloads/Teilnahmebedingungen-fuer-Ankaufsbonus-fuer-Samsung-Galaxy-Z-Flip8,-Fold8-und-Fold8-Ultra-01.pdf" target="_blank">Teilnahmebedingungen</a></li>
</ul></div>`,
    },
    '200&nbsp;€ Ankaufsbonus'
  ),
  // (https://i22-telekom-longtail.atlassian.net/browse/SM-175)
  ...badgeEntries(
    [
      // Samsung Galaxy Z Fold8
      // 256
      '99937772', // Graphite
      '99937774', // Cram
      '99937776', // Lavender

      // 512
      '99937778', // Graphite
      '99937781', // Cram
      '99937783', // Lavender

      // Samsung Galaxy Z Fold8 Ultra
      // 256
      '99937759', // Graphite
      '99937761', // Cream
      '99937763', // Violet Shadow

      // 512
      '99937765', // Graphite
      '99937768', // Cream
      '99937770', // Violet Shadow
    ],
    {
      text: '400&nbsp;€ Ankaufsbonus',
      footnoteText: `<div class="unstyled-html"><h4>Aktionsbedingungen</h4><br> <ul>
<li>Der Ankaufsbonus gilt für private Endkunden ab 18 Jahren bei Kauf eines Samsung Galaxy Z Flip8, Samsung Galaxy Z Fold8 oder Samsung Galaxy Z Fold8 Ultra vom 22.07.2026-06.08.2026 bei der Telekom Deutschland GmbH (TDG) und bei zusätzlichem Verkauf eines teilnahmeberechtigten Smartphones an die Assurant Deutschland GmbH. </li>
<li>Das Handyankaufsangebot für das Altgerät muss bis zum 06.08.2026 generiert werden und das Altgerät bis zum 27.08.2026 bei der Assurant Deutschland GmbH bzw. ihrem Dienstleister angekommen sein.</li>
<li>Teilnehmer erhalten ein Ankaufsangebot für das Altgerät, zzgl. eines Ankaufsbonus. 200&nbsp;€ Ankaufsbonus gibt es beim Kauf eines Samsung Galaxy Z Flip8 und 400&nbsp;€ Ankaufsbonus gibt es beim Kauf eines Samsung Galaxy Z Fold8 oder Samsung Galaxy Z Fold8 Ultra. Das Altgerät muss funktionsfähig sein, d.h. es muss ein- und ausschaltbar sowie entsperrt (kein SIM-/Net-Lock) sein, darf keinen Wasserschaden oder Displaybruch aufweisen und muss einen Mindestankaufswert von 1&nbsp;€ haben.</li>
<li>Teilnahme mit max. 3 Aktionsgeräten pro Haushalt. Solange der Vorrat reicht.</li>
<li>Ausrichter der Aktion ist Telekom Deutschland GmbH, Landgrabenweg 149, 53227 Bonn, Deutschland.</li>
<li>Hier finden Sie die <a href="/downloads/Teilnahmebedingungen-fuer-Ankaufsbonus-fuer-Samsung-Galaxy-Z-Flip8,-Fold8-und-Fold8-Ultra-01.pdf" target="_blank">Teilnahmebedingungen</a></li>
</ul></div>`,
    },
    '400&nbsp;€ Ankaufsbonus'
  ),
]);

export const useOfferStore = defineStore('offer', {
  state: (): OfferState => ({
    rebatesState: {},
    offerBadgesState: [],
    zeroPercentFinancingState: [],
    compaEanListState: [],
  }),
  getters: {
    rebates(state): Rebates {
      return state.rebatesState;
    },
    offerBadges(state): OfferBadgeEntry[] {
      return state.offerBadgesState;
    },
    zeroPercentFinancing(state): string[] {
      return state.zeroPercentFinancingState;
    },
    compaEanList(state): string[] {
      return state.compaEanListState;
    },
  },

  actions: {
    setRebates(rebates: Rebates) {
      this.rebatesState = rebates;
      this.offerBadgesState = [...rebatesToOfferBadges(rebates), ...manualOfferBadges.value];
    },

    setZeroPercentFinancing(articleNumbers: string[]) {
      this.zeroPercentFinancingState = articleNumbers;
    },

    setCompaEanList(compaEanList: string[]) {
      this.compaEanListState = compaEanList;
    },
  },
});
